import dynamic from 'next/dynamic';
import { Container, styled } from '@teko/ds-desktop';
import React from 'react';
import { Box, Flex } from 'reflexbox';
import { DiscoveryBlock } from '@teko/discovery';
import { findBlockByType, getBlockDomId } from 'helpers/block';
import { cleanObject } from '@teko/utils';
import { useRouter } from 'next/router';
import { ProductListWithFilter } from './ProductListWithFilter';
import { BannerProps } from '@teko/teko-ecommerce';
import { BannerEntity } from '@teko/ecommerce';
import { FilterInterface } from '@teko/types/ecommerce';
import { DiscoveryProduct } from 'types';

const DiscoveryRecommendedProducts = dynamic(() =>
  import('../../DiscoveryRecommendedProducts').then(
    (mod) => mod.DiscoveryRecommendedProducts,
  ),
);

const FeaturedBanners = dynamic(() =>
  import('@teko/teko-ecommerce').then((mod) => mod.FeaturedBanners),
);

const PromoteBlock = dynamic(() =>
  import('@teko/teko-ecommerce').then((mod) => mod.PromoteBlock),
);

const HotDealSection = dynamic(() =>
  import('./HotDealSection').then((mod) => mod.HotDealSection),
);

const BestSellingProductsBlock = dynamic(() =>
  import('./BestSellingProductsBlock').then(
    (mod) => mod.BestSellingProductsBlock,
  ),
);

const NavigationBarBlock = dynamic(() =>
  import('./NavigationBarBlock').then((mod) => mod.NavigationBarBlock),
);

const YoutubeBlock = dynamic(() =>
  import('./YoutubeBlock').then((mod) => mod.YoutubeBlock),
);

const LeftRightStickyBannerBlock = dynamic(() =>
  import('./LeftRightStickyBannerBlock').then(
    (mod) => mod.LeftRightStickyBannerBlock,
  ),
);

const ListCategories = dynamic(() =>
  import('@teko/teko-ecommerce').then((mod) => mod.ListCategories),
);
const FeaturedBrands = dynamic(() =>
  import('@teko/teko-ecommerce').then((mod) => mod.FeaturedBrands),
);

const FlashSaleBlock = dynamic(() =>
  import('./FlashSaleBlock').then((mod) => mod.FlashSaleBlock),
);

const BlockAnchor = styled.div`
  position: relative;
  top: -92px;
`;

interface DiscoveryBlocksProps {
  blocks: DiscoveryBlock[];
  customRenderer?: (block: DiscoveryBlock, index: number) => React.ReactNode;
  mapProducts?: { [key: string]: DiscoveryProduct[] }; // for SSR
  cookedFilters?: FilterInterface[];
}

export const DiscoveryBlocks: React.FC<DiscoveryBlocksProps> = ({
  blocks,
  customRenderer,
  mapProducts = {},
  cookedFilters = [],
}: DiscoveryBlocksProps) => {
  const categoryProductBlockRendered: string[] = [];
  const router = useRouter();
  const { query } = router;
  const mainSkuList = findBlockByType('MAIN_SKU_LIST', blocks);
  const leftStickyBanner = findBlockByType('LEFT_STICKY_BANNER', blocks);
  const rightStickyBanner = findBlockByType('RIGHT_STICKY_BANNER', blocks);
  const youtubeBlock = findBlockByType('YOUTUBE', blocks);
  const navigationBarBlock = findBlockByType('NAVIGATION_BAR', blocks);
  const isDefaultBlock = blocks.length === 2 && mainSkuList;

  if (isDefaultBlock) {
    const fetchParams = {
      ...mainSkuList.content.fetchParams,
      blockId: mainSkuList.id,
    };
    const filterParams = {
      ...fetchParams.filter,
      ...fetchParams.sorting,
      blockId: mainSkuList.id,
    } as any;
    return (
      <ProductListWithFilter
        marginTop="s16"
        filterParams={filterParams}
        params={cleanObject({ ...filterParams, ...query })}
        serverProducts={mapProducts[mainSkuList.id]}
        cookedFilters={cookedFilters}
      />
    );
  }

  return (
    <div>
      {navigationBarBlock && (
        <NavigationBarBlock block={navigationBarBlock} blockList={blocks} />
      )}
      {youtubeBlock && (
        <React.Fragment>
          <BlockAnchor id={getBlockDomId(youtubeBlock)} />
          <YoutubeBlock block={youtubeBlock} />
        </React.Fragment>
      )}
      {(leftStickyBanner || rightStickyBanner) && (
        <LeftRightStickyBannerBlock
          leftBlock={leftStickyBanner}
          rightBlock={rightStickyBanner}
          stickyTop={navigationBarBlock ? 112 : 72}
        />
      )}
      {blocks.map((block, index, list) => {
        const showBannerHorizontal =
          block.itemType === 'BANNER' &&
          block.type.startsWith('HORIZONTAL') &&
          block.type !== 'HORIZONTAL_4_BANNER' &&
          block?.cookedData?.length;
        const showCategoryProductBlock =
          block.type === 'MIX_BANNER' &&
          index > 1 &&
          !categoryProductBlockRendered.includes(list[index - 1].id) &&
          list[index - 1].type === 'MIX_BANNER';
        const showTopBrands =
          block.itemType === 'MENU' &&
          block.type === 'HORIZONTAL_HIGHLIGHTED_BANNER' &&
          block?.cookedData?.length;
        const showCustomCategories =
          block.type === 'PROMOTE_CATEGORY_MENU' && block?.cookedData?.length;
        if (showCustomCategories) {
          return (
            <Container marginBottom="s24" key={index}>
              <BlockAnchor id={getBlockDomId(block)} />
              <ListCategories
                categories={block.cookedData
                  .slice(0, 10)
                  .map((category: BannerEntity) => {
                    return {
                      title: category.campaignName,
                      image: { src: category.image },
                      link: { href: category.targetPath },
                    };
                  })}
                header={{ title: block.label }}
              />
            </Container>
          );
        }
        if (showTopBrands)
          return (
            <Container marginBottom="s24" key={index}>
              <BlockAnchor id={getBlockDomId(block)} />
              <FeaturedBrands
                header={{
                  title: block.label,
                }}
                brands={block.cookedData.map((bl: BannerEntity) => ({
                  name: bl.campaignName,
                  description: bl.description,
                  image: {
                    src: bl.image,
                  },
                  link: {
                    href: bl.targetPath,
                  },
                }))}
              />
            </Container>
          );
        if (showCategoryProductBlock) {
          categoryProductBlockRendered.push(block.id);
          categoryProductBlockRendered.push(list[index - 1].id);
          const categoryBlock1 = list[index - 1].cookedData;
          const categoryBlock2 = block.cookedData;
          return (
            <Container key={index}>
              <BlockAnchor id={getBlockDomId(block)} />
              <Flex marginBottom={24} flexWrap="wrap" mx={-2}>
                <Box width={[1, 1 / 2]} px={2}>
                  <PromoteBlock {...categoryBlock1} />
                </Box>
                <Box width={[1, 1 / 2]} px={2}>
                  <PromoteBlock {...categoryBlock2} />
                </Box>
              </Flex>
            </Container>
          );
        }
        if (showBannerHorizontal) {
          return (
            <Container marginBottom="s24" key={index}>
              <BlockAnchor id={getBlockDomId(block)} />
              <FeaturedBanners
                regionName="bodyBannerHomepage"
                banners={block.cookedData.map((banner): BannerProps => {
                  return {
                    src: banner.image,
                    link: {
                      href: banner.targetPath,
                    },
                    name: banner.campaignName,
                  };
                })}
              />
            </Container>
          );
        }

        if (customRenderer) {
          // for other block types
          // allow an input customRenderer to overwrite
          // the default input
          const blockNode = customRenderer(block, index);
          if (blockNode) return blockNode;
        }

        if (
          block.type === 'COUNT_DOWN_PROMO_SKU' ||
          block.type === 'FLASH_SALE'
        ) {
          return (
            <React.Fragment key={index}>
              <BlockAnchor id={getBlockDomId(block)} />
              <FlashSaleBlock
                block={block}
                serverProducts={mapProducts[block.id]}
              />
            </React.Fragment>
          );
        }
        if (
          block.type === 'HORIZONTAL_SKU_LIST' ||
          block.type === 'BEST_SELLING_PRODUCT'
        ) {
          return (
            <Container marginBottom="s24" key={index}>
              <BlockAnchor id={getBlockDomId(block)} />
              <BestSellingProductsBlock
                block={block}
                serverProducts={mapProducts[block.id]}
              />
            </Container>
          );
        }
        if (block.type === 'COUNT_DOWN_PROMO_BIG_SKU') {
          const serverItemId = block?.cookedData?.[0]?.paramsObject?.itemId;
          const serverProducts = mapProducts[`${block.id}_${serverItemId}`];
          return (
            <Container marginBottom="s24" key={index}>
              <BlockAnchor id={getBlockDomId(block)} />
              <HotDealSection block={block} serverProducts={serverProducts} />
            </Container>
          );
        }
        if (block.type === 'MAIN_SKU_LIST' || block.type === 'PROMOTE_SELLER') {
          return (
            <Container marginBottom="s24" key={index}>
              <BlockAnchor id={getBlockDomId(block)} />
              <DiscoveryRecommendedProducts
                title={block.label}
                paramsObject={{
                  ...block.cookedData.paramsObject,
                  blockId: block.id,
                }}
                actionLink={
                  block.viewMoreUrl ? { href: block.viewMoreUrl } : undefined
                }
                serverProducts={mapProducts[block.id]}
              />
            </Container>
          );
        }
      })}
    </div>
  );
};
